import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { generateListResponseScheme, type ListRespondedWith } from '@/models/response';
import { fetcher } from '@/utils/fetcher';
import { avatarUrlSchema, stringifiedDateSchema } from '@/utils/schemas';

export const blacklistQueryOptions = queryOptions({
  queryKey: ['/GetBlackList'],
  queryFn: ({ signal }) =>
    fetcher<ListRespondedWith<BlacklistMember>>('/GetBlackList', undefined, {
      signal,
      schema: generateListResponseScheme(blacklistMemberScheme),
      validResponseCodes: [0, 1],
    }).then((response) => response?.Data ?? []),
  placeholderData: [],
  staleTime: 0,
});

export default function useBlacklist() {
  const query = useQuery(blacklistQueryOptions);

  return {
    blacklist: query.data ?? [],
    ...query,
  };
}

export function useBlacklisted(targetUserId?: number | undefined) {
  return useQuery({
    ...blacklistQueryOptions,
    enabled: !!targetUserId,
    select: (data) => data?.some((user) => user.BlackUserID === targetUserId) ?? false,
  });
}

const blacklistMemberScheme = z.object({
  ID: z.number(),
  UserID: z.number(),
  BlackUserID: z.number(),
  BlackUserHeadImg: avatarUrlSchema,
  BlackUserNickName: z.string(),
  CreateTime: stringifiedDateSchema,
});

export type BlacklistMember = z.infer<typeof blacklistMemberScheme>;
